import sumBy from 'lodash/sumBy';

import { roundWithDecimals } from '@commons/utils/math';

import { getProductPriceExcludingVAT } from '@admin/products/products/detail/components/ProductInfo/utils/getProductPriceExcludingVAT.js';

/**
 * Normalizes composition wrappers (`{ entity, quantity, channelIds | channels }`) into a flat shape
 * the cost computation can consume directly: entity fields at the top level, `quantity`
 * preserved, and `channelIds: string[]` materialized as `channels: { [channelId]: boolean }`
 * so the per-channel lookup in `computeTotalCostByChannel` is O(1).
 */
export const normalizeCompositions = (compositions, channels) => {
  if (!Array.isArray(compositions) || compositions.length === 0) {
    return [];
  }

  return compositions
    .map((composition) => {
      const { entity, quantity, channelIds, channels: compositionChannels } = composition || {};

      if (!entity) {
        return null;
      }

      const resolvedChannelIds = channelIds ?? (compositionChannels ?? []).map(({ id }) => id);

      const channelsMap = {};

      channels.forEach((channel) => {
        channelsMap[channel.id] = resolvedChannelIds.includes(channel.id);
      });

      return {
        ...entity,
        quantity,
        channels: channelsMap,
      };
    })
    .filter(Boolean);
};

export const getEntityCostForDefaultChannel = (entity, defaultChannelId) => {
  const quantity = entity.quantity ?? 0;

  if (entity.isIngredient) {
    const unitCost = entity.cost ?? entity.costByChannelId?.[defaultChannelId] ?? 0;

    return unitCost * quantity;
  }

  return (entity.costByChannelId?.[defaultChannelId] ?? 0) * quantity;
};

export const computeTotalCostByChannel = (entities, recipe, channels, defaultChannelId) => {
  const entitiesWithUpdatedCost = entities.map((entity) => ({
    ...entity,
    cost: getEntityCostForDefaultChannel(entity, defaultChannelId),
  }));

  if (!recipe.hasMultipleChannels) {
    return {
      [defaultChannelId]: sumBy(entitiesWithUpdatedCost, 'cost'),
    };
  }

  return entitiesWithUpdatedCost.reduce((acc, entity) => {
    channels.forEach((channel) => {
      if (!acc[channel.id]) {
        acc[channel.id] = 0;
      }

      if (entity.isIngredient) {
        acc[channel.id] += entity.channels[channel.id] ? entity.cost : 0;
        return;
      }

      if (!entity.hasMultipleChannels) {
        // A sub-recipe with a uniform composition has the same cost on every channel, so it must
        // be attributed to each channel its composition line belongs to (like an ingredient),
        // reading the default-channel value since non-default keys may be absent for such recipes.
        if (!entity.channels[channel.id]) {
          return;
        }

        acc[channel.id] += (entity.costByChannelId?.[defaultChannelId] ?? 0) * entity.quantity;
        return;
      }

      if (!entity.channels[channel.id]) {
        return;
      }

      acc[channel.id] += (entity.costByChannelId?.[channel.id] ?? 0) * entity.quantity;
    });

    return acc;
  }, {});
};

/**
 * Returns the raw cost/price ratio (in %) for a given channel. Returns null when the product
 * has no price for the channel or when cost is missing.
 *
 * Uses `roundWithDecimals` rather than `formatNumber` because the latter produces a display
 * string with a non-breaking space as thousands separator — coercing it back to a number for
 * arithmetic yields `NaN` as soon as values reach 1 000.
 */
export const computeRatio = (cost, channelId, associatedProduct, numberDecimals) => {
  const productPriceToUse = +getProductPriceExcludingVAT(associatedProduct, channelId) || null;

  if (!productPriceToUse || parseFloat(productPriceToUse) === 0) {
    return null;
  }

  if (!cost) {
    return null;
  }

  const roundedCost = roundWithDecimals(cost, numberDecimals);
  const roundedPrice = roundWithDecimals(productPriceToUse, numberDecimals);

  return (roundedCost / roundedPrice) * 100;
};

/**
 * Returns the raw margin ratio (in %) for a given channel, i.e. `(price - cost) / price * 100`.
 * Returns null when the product has no price for the channel or when cost is missing.
 */
export const computeMargin = (cost, channelId, associatedProduct, numberDecimals) => {
  const priceToUse = +getProductPriceExcludingVAT(associatedProduct, channelId) || null;

  if (!priceToUse || parseFloat(priceToUse) === 0) {
    return null;
  }

  if (!cost) {
    return null;
  }

  const roundedCost = roundWithDecimals(cost, numberDecimals);

  return ((priceToUse - roundedCost) / priceToUse) * 100;
};
